
import { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useLocation, useSearchParams } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowDown, ArrowUp, FileText } from "lucide-react";
import { BankAccountsStatus, Receivable, ReceivableFilters, ReceivableStatus } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import PageHeaderActions from "@/components/common/PageHeaderActions";
import { formatCurrency } from "@/utils/paymentUtils";
import { useStatusBadge } from "@/i18n/useStatusBadge";
import { useMatchRefusalTitle } from "@/i18n/useMatching";
import ReceivablesTable from "@/components/payments/ReceivablesTable";
import ReceivableFiltersComponent from "@/components/payments/ReceivableFiltersComponent";
import InvoiceDetailsDialog from "@/components/payments/InvoiceDetailsDialog";
import MarkAsReceivedModal from "@/components/receivables/MarkAsReceivedModal";
import { useInvoiceDetailsDialog } from "@/hooks/useInvoiceDetailsDialog";
import { useBankAccounts } from "@/hooks/useBankAccounts";
import { ManualMatchApiClient } from "@/clients/manualMatchApiClient";
import { settledRefusalMessage } from "@/utils/errorUtils";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import React from "react";
import { FilePlus2 } from "lucide-react";

const AccountReceivables = () => {
  const t = useTranslations("consultantReceivables");
  const tStatus = useTranslations("consultantPayables.status");
  const tRefusals = useTranslations("consultantMatching.refusals");
  const getStatusBadge = useStatusBadge();
  const matchRefusalTitle = useMatchRefusalTitle();
  // The latest translator, so the loaders below can word a toast without being re-run when it changes.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [receivables, setReceivables] = useState<Receivable[]>([]);
  const [selectedReceivables, setSelectedReceivables] = useState<string[]>([]);
  const [receivableSortConfig, setReceivableSortConfig] = useState<{
    key: keyof Receivable | null;
    direction: 'ascending' | 'descending';
  }>({ key: null, direction: 'ascending' });
  const [showReceivableFilters, setShowReceivableFilters] = useState(false);
  const [receivableFilters, setReceivableFilters] = useState<ReceivableFilters>({
    status: 'all',
    customer: '',
    minAmount: '',
    maxAmount: '',
    currency: 'all',
    matched: 'all'
  });

  // Bank accounts for determining modal mode. #498 whole-branch review finding I2: must
  // distinguish "still loading" / "fetch failed or bank consent expired" from "genuinely no
  // accounts connected" — see BankAccountsStatus's docstring (src/types/payments.ts). Silently
  // treating loading/error as `false` would route a company whose accounts ARE connected through
  // the trusted, unaudited "received externally" write with no auto_match_decisions row at all.
  const { accounts, loadingAccounts, accountsError, consentRequiredError } = useBankAccounts();
  const hasBankAccounts: BankAccountsStatus = loadingAccounts
    ? "loading"
    : accountsError || consentRequiredError
    ? "error"
    : accounts.length > 0;

  // Mark as received modal state
  const [markAsReceivedOpen, setMarkAsReceivedOpen] = useState(false);
  const [selectedReceivableForAction, setSelectedReceivableForAction] = useState<Receivable | null>(null);

  // Use the invoice details dialog hook
  const {
    detailsDialogOpen,
    selectedInvoice,
    setDetailsDialogOpen,
    handleRowClick
  } = useInvoiceDetailsDialog();

  // Get receivable ID from URL parameters
  const receivableIdFromUrl = searchParams.get('id');

  // Fetch receivables on mount. issue #819: getReceivables() now propagates a real API
  // failure instead of silently falling back to fabricated mock data — catch it here so the
  // consultant sees a real error toast and an empty list, not an uncaught rejection.
  React.useEffect(() => {
    const fetchReceivables = async () => {
      try {
        const service = ServiceRegistry.getReceivableService();
        const data = await service.getReceivables();
        setReceivables(data);
      } catch (error) {
        console.error("Failed to load receivables:", error);
        toast.error(tRef.current("toasts.errorTitle"), { description: tRef.current("toasts.loadFailed") });
        setReceivables([]);
      }
    };
    fetchReceivables();
  }, []);

  // Handle URL parameter for specific receivable
  useEffect(() => {
    if (receivableIdFromUrl && receivables.length > 0) {
      const targetReceivable = receivables.find(r => r.id === receivableIdFromUrl);
      if (targetReceivable) {
        // Set the tab based on the receivable's status
        setActiveTab(targetReceivable.status);
        
        // Show a toast to indicate we've found and highlighted the receivable
        toast(tRef.current("toasts.foundTitle"), {
          description: tRef.current("toasts.foundBody", {
            reference: targetReceivable.remittanceInformation,
            customer: targetReceivable.customer,
          }),
        });
      } else {
        toast.error(tRef.current("toasts.notFoundTitle"), { description: tRef.current("toasts.notFoundBody") });
      }
    }
  }, [receivableIdFromUrl, receivables]);

  // Get unique currencies for filter dropdown
  const uniqueReceivableCurrencies = useMemo(() => {
    return Array.from(new Set(receivables.map(receivable => receivable.currency)));
  }, [receivables]);

  // Toggle row selection for receivables
  const toggleSelectReceivable = (receivableId: string) => {
    setSelectedReceivables(prevSelected => 
      prevSelected.includes(receivableId)
        ? prevSelected.filter(id => id !== receivableId)
        : [...prevSelected, receivableId]
    );
  };

  // Toggle select all receivables
  const toggleSelectAllReceivables = () => {
    if (selectedReceivables.length === filteredReceivables.length) {
      setSelectedReceivables([]);
    } else {
      setSelectedReceivables(filteredReceivables.map(receivable => receivable.id));
    }
  };

  // Handle receivable filter changes
  const handleReceivableFilterChange = (key: keyof ReceivableFilters, value: string) => {
    setReceivableFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Reset all receivable filters
  const resetReceivableFilters = () => {
    setReceivableFilters({
      status: 'all',
      customer: '',
      minAmount: '',
      maxAmount: '',
      currency: 'all',
      matched: 'all'
    });
  };

  // Update filters when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update the status filter based on the selected tab
    if (value !== "all") {
      setReceivableFilters(prev => ({
        ...prev,
        status: value as ReceivableStatus
      }));
    } else {
      setReceivableFilters(prev => ({
        ...prev,
        status: 'all'
      }));
    }
  };

  
  // Handle refresh of all data
  const handleRefreshClick = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    toast(t("toasts.refreshingTitle"), { description: t("toasts.refreshingBody") });
    
    try {
      // In a real app, you would fetch new data here
      // For mock data, we'll just simulate a refresh
      setTimeout(() => {
        // Pretend we've refreshed the data
        toast.success(t("toasts.refreshDoneTitle"), { description: t("toasts.refreshDoneBody") });
        setRefreshing(false);
      }, 1500);
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error(t("toasts.refreshFailedTitle"), {
        description: t("toasts.refreshFailedBody"),
      });
      setRefreshing(false);
    }
  };

  // Convert receivable to invoice format for the details dialog
  const convertReceivableToInvoice = (receivable: Receivable) => {
    return {
      id: receivable.id,
      status: receivable.status as any, // Receivable status maps to invoice status
      vendor: receivable.customer, // Customer is the "vendor" for receivables
      amount: receivable.amount,
      fxRate: receivable.fxRate,
      currency: receivable.currency,
      dueDate: receivable.dueDate,
      documentType: 'receivable' as const, // Receivables are incoming invoices
      remittanceInformation: receivable.remittanceInformation,
      // Add file information if available
      // A payout receivable (FIN-977) has no file: its evidence is the tax account row.
      filePath: receivable.tax ? undefined : receivable.filePath || `invoices/${receivable.id}.pdf`, // Use receivable ID as fallback
      fileName: receivable.tax ? undefined : receivable.fileName || `${receivable.remittanceInformation}.pdf`, // Use remittance info as fallback
      tax: receivable.tax,
      // #498 Task 6 — carry the matched/unmatched_acknowledged status through so
      // InvoiceDetailsDialog's audit-trail section (AC11) renders for receivables too, not
      // just payables.
      paymentVerification: receivable.paymentVerification,
    };
  };

  // Handle view action for receivables
  const handleViewReceivable = (receivableId: string) => {
    const receivable = receivables.find(r => r.id === receivableId);
    if (receivable) {
      // Convert receivable to invoice format and open dialog
      const invoiceData = convertReceivableToInvoice(receivable);
      handleRowClick(invoiceData);
    }
  };

  // Handle "Mark as received" action
  const handleMarkAsReceived = (receivable: Receivable) => {
    setSelectedReceivableForAction(receivable);
    setMarkAsReceivedOpen(true);
  };

  // Refresh receivables data from server
  const refreshReceivables = async () => {
    const service = ServiceRegistry.getReceivableService();
    const data = await service.getReceivables();
    setReceivables(data);
  };

  // Handle match transaction (receivable → transaction)
  const handleMatchTransaction = async (receivableId: string, transactionId: string) => {
    try {
      await ManualMatchApiClient.createManualMatch({ transactionId, receivableId, paymentId: '' });

      toast.success(t("toasts.matchedTitle"), {
        description: t("toasts.matchedBody"),
      });
      await refreshReceivables();
    } catch (error) {
      console.error("Failed to match transaction:", error);
      // FIN-968 (widened by FIN-1023 D7/AC-13): a permanent refusal is named, never "please try again".
      const settled = settledRefusalMessage(error);
      const title = settled ? matchRefusalTitle(error) ?? tRefusals("transactionAlreadySettled") : t("toasts.errorTitle");
      toast.error(title, {
        description: settled ?? t("toasts.matchFailed"),
      });
    }
  };

  // Handle received externally (date picker)
  const handleReceivedExternally = async (receivableId: string, date: string, actualSekAmount?: number) => {
    try {
      const service = ServiceRegistry.getReceivableService();
      await service.markAsReceivedExternally(receivableId, date, actualSekAmount);
      toast.success(t("toasts.receivedTitle"), {
        description: t("toasts.receivedBody"),
      });
      await refreshReceivables();
    } catch (error) {
      console.error("Failed to mark as received externally:", error);
      toast.error(t("toasts.errorTitle"), { description: t("toasts.receivedFailed") });
    }
  };

  // Handle the zero-match / "None of these match" acknowledgment write (issue #498, Task 4 —
  // MarkAsReceivedModal's case c). Distinct from handleReceivedExternally above: this always
  // routes through ReceivableService.acknowledgeExternalPayment, which writes an audited
  // auto_match_decisions row on the backend, rather than the trusted no-audit case-a write.
  const handleAcknowledgeExternalPayment = async (receivableId: string, date: string) => {
    try {
      const service = ServiceRegistry.getReceivableService();
      await service.acknowledgeExternalPayment(receivableId, date);
      toast.success(t("toasts.receivedTitle"), {
        description: t("toasts.acknowledgedBody"),
      });
      await refreshReceivables();
    } catch (error) {
      console.error("Failed to acknowledge external payment:", error);
      toast.error(t("toasts.errorTitle"), { description: t("toasts.acknowledgedFailed") });
    }
  };

  // Filter receivables based on current filter configuration
  const filteredReceivables = useMemo(() => {
    return receivables.filter(receivable => {
      // Filter by status (now controlled by tabs)
      if (activeTab !== 'all' && receivable.status !== activeTab) {
        return false;
      }
      
      // Filter by customer
      if (receivableFilters.customer && !receivable.customer.toLowerCase().includes(receivableFilters.customer.toLowerCase())) {
        return false;
      }
      
      // Filter by min amount
      if (receivableFilters.minAmount && receivable.amount < parseFloat(receivableFilters.minAmount)) {
        return false;
      }
      
      // Filter by max amount
      if (receivableFilters.maxAmount && receivable.amount > parseFloat(receivableFilters.maxAmount)) {
        return false;
      }
      
      // Filter by currency
      if (receivableFilters.currency !== 'all' && receivable.currency !== receivableFilters.currency) {
        return false;
      }
      
      // Filter by matched status (only for paid receivables)
      if (receivableFilters.matched !== 'all') {
        if (receivable.status !== 'paid') {
          return receivableFilters.matched === 'false'; // Show unpaid/overdue only when filter is "not matched"
        }
        
        const isMatched = receivable.matched === true;
        return receivableFilters.matched === 'true' ? isMatched : !isMatched;
      }
      
      return true;
    });
  }, [receivables, receivableFilters, activeTab]);

  // Sort receivables based on current sort configuration
  const sortedReceivables = useMemo(() => {
    if (!receivableSortConfig.key) return filteredReceivables;
    
    return [...filteredReceivables].sort((a, b) => {
      if (a[receivableSortConfig.key!] < b[receivableSortConfig.key!]) {
        return receivableSortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[receivableSortConfig.key!] > b[receivableSortConfig.key!]) {
        return receivableSortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredReceivables, receivableSortConfig]);

  // Handle column header click for sorting receivables
  const handleReceivableSort = (key: keyof Receivable) => {
    setReceivableSortConfig(prevConfig => ({
      key,
      direction: 
        prevConfig.key === key && prevConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending'
    }));
  };

  // Render sort icon for receivables
  const renderReceivableSortIcon = (key: keyof Receivable) => {
    if (receivableSortConfig.key !== key) return null;
    return receivableSortConfig.direction === 'ascending' ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t("page.title")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="flex items-center gap-2" onClick={() => navigate("/create-invoice")}>
            <FilePlus2 size={18} />
            {t("page.createInvoice")}
          </Button>
          <Button variant="outline" className="flex items-center gap-2" onClick={() => navigate("/invoices")}>
            <FileText size={18} />
            {t("page.invoices")}
          </Button>
          <PageHeaderActions onRefresh={handleRefreshClick} refreshing={refreshing} />
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{t("page.title")}</CardTitle>
          <CardDescription>
            {t("page.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" value={activeTab} onValueChange={handleTabChange}>
            <div className="flex justify-between items-center mb-4">
              <TabsList className="mb-2">
                <TabsTrigger value="all" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {tStatus("all")}
                </TabsTrigger>
                <TabsTrigger value="unpaid" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {tStatus("unpaid")}
                </TabsTrigger>
                <TabsTrigger value="paid" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {tStatus("paid")}
                </TabsTrigger>
                <TabsTrigger value="overdue" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {tStatus("overdue")}
                </TabsTrigger>
              </TabsList>
            
              {/* Filter Controls */}
              <ReceivableFiltersComponent 
                filters={receivableFilters}
                handleFilterChange={handleReceivableFilterChange}
                resetFilters={resetReceivableFilters}
                uniqueCurrencies={uniqueReceivableCurrencies}
                showFilters={showReceivableFilters}
                setShowFilters={setShowReceivableFilters}
              />
            </div>
            
            <TabsContent value="all" className="mt-0">
              {/* Receivables Table - All tab (no checkboxes) */}
              <ReceivablesTable 
                sortedReceivables={sortedReceivables}
                selectedReceivables={[]} // Empty array to disable selection
                toggleSelectReceivable={() => {}} // No-op function
                toggleSelectAll={() => {}} // No-op function
                handleSort={handleReceivableSort}
                renderSortIcon={renderReceivableSortIcon}
                handleView={handleViewReceivable}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
                filteredReceivables={filteredReceivables}
                hideCheckboxes={true}
                highlightReceivableId={receivableIdFromUrl}
                onMarkAsReceived={handleMarkAsReceived}
              />
            </TabsContent>

            <TabsContent value="unpaid" className="mt-0">
              {/* Receivables Table - Unpaid tab */}
              <ReceivablesTable
                sortedReceivables={sortedReceivables}
                selectedReceivables={selectedReceivables}
                toggleSelectReceivable={toggleSelectReceivable}
                toggleSelectAll={toggleSelectAllReceivables}
                handleSort={handleReceivableSort}
                renderSortIcon={renderReceivableSortIcon}
                handleView={handleViewReceivable}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
                filteredReceivables={filteredReceivables}
                highlightReceivableId={receivableIdFromUrl}
                onMarkAsReceived={handleMarkAsReceived}
              />
            </TabsContent>

            <TabsContent value="paid" className="mt-0">
              {/* Receivables Table - Paid tab */}
              <ReceivablesTable
                sortedReceivables={sortedReceivables}
                selectedReceivables={selectedReceivables}
                toggleSelectReceivable={toggleSelectReceivable}
                toggleSelectAll={toggleSelectAllReceivables}
                handleSort={handleReceivableSort}
                renderSortIcon={renderReceivableSortIcon}
                handleView={handleViewReceivable}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
                filteredReceivables={filteredReceivables}
                highlightReceivableId={receivableIdFromUrl}
                onMarkAsReceived={handleMarkAsReceived}
              />
            </TabsContent>

            <TabsContent value="overdue" className="mt-0">
              {/* Receivables Table - Overdue tab */}
              <ReceivablesTable
                sortedReceivables={sortedReceivables}
                selectedReceivables={selectedReceivables}
                toggleSelectReceivable={toggleSelectReceivable}
                toggleSelectAll={toggleSelectAllReceivables}
                handleSort={handleReceivableSort}
                renderSortIcon={renderReceivableSortIcon}
                handleView={handleViewReceivable}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
                filteredReceivables={filteredReceivables}
                highlightReceivableId={receivableIdFromUrl}
                onMarkAsReceived={handleMarkAsReceived}
              />
            </TabsContent>
          </Tabs>
          
          {/* Results summary */}
          {sortedReceivables.length > 0 && (
            <div className="mt-2 text-sm text-muted-foreground">
              {t("summary.showing", { count: sortedReceivables.length })}
              {(receivableFilters.customer || receivableFilters.minAmount || receivableFilters.maxAmount || receivableFilters.currency !== 'all' || receivableFilters.matched !== 'all') &&
                t("summary.filteredFrom", { total: String(receivables.length) })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <InvoiceDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        invoice={selectedInvoice}
        formatCurrency={formatCurrency}
      />

      {/* Mark as Received Modal */}
      <MarkAsReceivedModal
        open={markAsReceivedOpen}
        setOpen={setMarkAsReceivedOpen}
        receivable={selectedReceivableForAction}
        hasBankAccounts={hasBankAccounts}
        onMatchTransaction={handleMatchTransaction}
        onMarkReceivedExternally={handleReceivedExternally}
        onAcknowledgeExternalPayment={handleAcknowledgeExternalPayment}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default AccountReceivables;
